import {graphql} from 'gatsby'
import React from 'react'

import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import {Showcase} from '@/components/showcase'
import {Main} from '@/components/skip-to-content'
import {useGlobalStrings} from '@/providers/global-strings'
import {SliceZone} from '@/slices/slice-zone'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {isEmptyObject} from '@/utils/is-empty-object'
import {withPrismicPreview} from '@/utils/with-prismic-preview'

import type {Props as ShowcaseProps} from '@/components/showcase'
import type {CustomerCaseQuery} from '@/graphql-types'
import type {GatsbyHeadProps} from '@/types/gatsby-head'
import type {PageProps} from 'gatsby'

type Props = PageProps<CustomerCaseQuery>

const CustomerCaseTemplate = (props: Props) => {
    const slices = props.data.allPrismicTemplateCustomerCase.nodes[0]?.data
    const pageData = props.data.allPrismicCustomerCase.nodes[0]?.data
    const {employees, industry, location, show_more} = useGlobalStrings()

    if (!pageData) {
        return handleNoPrismicData()
    }

    const shouldReplaceCardGrid = !isEmptyObject(pageData.related_customer_cases)
    const shouldReplaceMediaItemGrid = !isEmptyObject(pageData.media_item_grid)

    slices?.body.forEach((slice, index) => {
        if (
            shouldReplaceCardGrid &&
            slice.__typename === 'PrismicTemplateCustomerCaseDataBodyCardGrid'
        ) {
            slices.body[index] = {
                ...slice,
                items: pageData.related_customer_cases.map((item) => ({
                    ...item,
                    ctaLink: item.cta_link,
                    title: item.case_title,
                    tag_text: '',
                    tag_color: '',
                    filterKeywords: '',
                })),
                primary: {
                    ...slice.primary,
                    show_more_text: show_more,
                    blockTitle: {richText: pageData.related_customer_cases_title},
                },
            }
        } else if (
            shouldReplaceMediaItemGrid &&
            slice.__typename === 'PrismicTemplateCustomerCaseDataBodyMediaItemGrid'
        ) {
            slices.body[index] = {
                ...slice,
                items: pageData.media_item_grid.map((item) => ({
                    ...item,
                    item_title: item.title,
                    item_description: item.body,
                    image: item.icon,
                })),
                primary: {
                    ...slice.primary,
                    title: pageData.media_item_grid_title,
                },
            }
        }
    })

    const mappedFields: ShowcaseProps['data'] = {
        hero: {
            title: pageData.hero_section[0].title,
            preTitle: pageData.hero_section[0].pre_title,
            image: pageData.hero_section[0].image,
            videoId: pageData.hero_section[0].wistia_video_id,
        },
        card: {
            logo: pageData.customer_card[0].logo,
            quote: pageData.customer_card[0].quote,
            quote_author: pageData.customer_card[0].quote_author,
            quote_author_image: pageData.customer_card[0].quote_author_image,
            details: [
                {
                    title: industry,
                    body: pageData.customer_card[0].industry,
                },
                {
                    title: location,
                    body: pageData.customer_card[0].location,
                },
                {
                    title: employees,
                    body: pageData.customer_card[0].employees,
                },
            ],
        },
        challengeLeadText: pageData.challenge_lead_text.richText,
        challengeTitle: pageData.challenge_title,
        challengeList: pageData.challenge_list.map((listItem) => ({
            challenge_list_item_title: listItem.challenge_list_item_title,
            challenge_list_item_body: listItem.challenge_list_item_body.richText,
        })),
        solutionLeadText: pageData.solution_lead_text.richText,
        solutionTitle: pageData.solution_title,
        resultLeadText: pageData.result_lead_text.richText,
        resultTitle: pageData.result_title,
        resultList: pageData.result_list.map((listItem) => ({
            result_list_item_title: listItem.result_list_item_title,
            result_list_item_body: listItem.result_list_item_body.richText,
        })),
    }

    return (
        <>
            <Header />

            <Main>
                <Showcase data={mappedFields} />

                {slices?.body && <SliceZone slices={slices.body} />}
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<CustomerCaseQuery>) => {
    const pageData = data.allPrismicTemplateCustomerCase.nodes[0].data
    const customerCaseData = data.allPrismicCustomerCase.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={customerCaseData.meta_title}
            description={customerCaseData.meta_description || pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image.url}
        />
    )
}

export const query = graphql`
    query CustomerCase($uid: String!, $locale: String!) {
        allPrismicCustomerCase(filter: {uid: {eq: $uid}, lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    hero_section {
                        pre_title
                        title
                        wistia_video_id
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                    }
                    customer_card {
                        logo {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        quote_author_image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        location
                        industry
                        employees
                        quote
                        quote_author
                    }
                    challenge_title
                    challenge_lead_text {
                        richText
                    }
                    challenge_list {
                        challenge_list_item_title
                        challenge_list_item_body {
                            richText
                        }
                    }
                    result_title
                    result_lead_text {
                        richText
                    }
                    result_list {
                        result_list_item_title
                        result_list_item_body {
                            richText
                        }
                    }
                    solution_title
                    solution_lead_text {
                        richText
                    }
                    media_item_grid_title
                    media_item_grid {
                        title
                        body {
                            richText
                        }
                        icon {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                    }
                    related_customer_cases_title
                    related_customer_cases_cta_text
                    related_customer_cases {
                        body
                        case_title
                        cta_link {
                            ...PrismicLink
                        }
                        image {
                            dimensions {
                                height
                                width
                            }
                            alt
                            copyright
                            url
                            gatsbyImageData(
                                breakpoints: [60, 80, 120, 180]
                                sizes: "(max-width: 767px) 100vw, (max-width: 991px) 40vw, 55vw"
                            )
                        }
                    }
                }
            }
        }

        allPrismicTemplateCustomerCase(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    body {
                        ...CustomerCaseMediaItemGrid
                        ...CustomerCaseGetStarted
                        ...CustomerCaseCardGrid
                    }
                }
            }
        }
    }

    fragment CustomerCaseMediaItemGrid on PrismicTemplateCustomerCaseDataBodyMediaItemGrid {
        __typename
        primary {
            title
            layout
            background
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            item_title
            item_description {
                richText
            }
        }
    }

    fragment CustomerCaseGetStarted on PrismicTemplateCustomerCaseDataBodyGetStarted {
        __typename
        primary {
            title
            error_message
            button_label
            input_placeholder
            form_subtitle
            aria_label
            form_production {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            form_staging {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            image {
                alt
                copyright
                url
                dimensions {
                    width
                    height
                }
            }
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
        }
    }

    fragment CustomerCaseCardGrid on PrismicTemplateCustomerCaseDataBodyCardGrid {
        __typename
        primary {
            blockTitle: title {
                richText
            }
            subtitle
            show_more_text
            initial_cards_shown
            initial_cards_shown_on_mobile
            initial_cards_shown_on_desktop
            card_cta_text
            show_filter
            layout
            background_color
            no_padding
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            title
            body
            ctaLink: cta_link {
                ...PrismicLink
            }
            filterKeywords: filter_keywords
            tag_text
            tag_color
        }
    }
`

export default withPrismicPreview(CustomerCaseTemplate)
